class TabsComponent extends HTMLElement {
  #controller = new AbortController();
  #observer;

  connectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();

    this.#synchronizeTabs();

    const { signal } = this.#controller;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    const panelsContainer = this.#panelsContainer;
    if (panelsContainer) {
      this.#observer = new MutationObserver(() => this.#synchronizeTabs());
      this.#observer.observe(panelsContainer, { childList: true });
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
  }

  get #tabList() {
    const tabList = this.querySelector(':scope > [data-tabs-list]');
    return tabList instanceof HTMLElement ? tabList : null;
  }

  get #panelsContainer() {
    const panels = this.querySelector(':scope > [data-tabs-panels]');
    return panels instanceof HTMLElement ? panels : null;
  }

  get #items() {
    const panels = this.#panelsContainer;
    if (!panels) return [];

    return Array.from(panels.querySelectorAll(':scope > [data-tab-item]')).filter(
      (item) => item instanceof HTMLElement
    );
  }

  get #tabs() {
    const tabList = this.#tabList;
    if (!tabList) return [];

    return Array.from(tabList.querySelectorAll(':scope > [role="tab"]')).filter(
      (tab) => tab instanceof HTMLButtonElement
    );
  }

  get #panels() {
    return this.#items
      .map((item) => item.querySelector(':scope > [role="tabpanel"]'))
      .filter((panel) => panel instanceof HTMLElement);
  }

  #synchronizeTabs() {
    const tabList = this.#tabList;
    if (!tabList) return;

    const activePanelId = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.tabTarget;
    const validPanelIds = new Set();

    for (const item of this.#items) {
      const panel = item.querySelector(':scope > [role="tabpanel"]');
      if (!(panel instanceof HTMLElement) || !panel.id) continue;

      validPanelIds.add(panel.id);

      const nestedTab = item.querySelector(':scope > [role="tab"]');
      const existingTabs = Array.from(tabList.querySelectorAll(`[data-tab-target="${panel.id}"]`));
      const tab = nestedTab instanceof HTMLButtonElement ? nestedTab : existingTabs[0];

      for (const duplicateTab of existingTabs) {
        if (duplicateTab !== tab) duplicateTab.remove();
      }

      if (tab instanceof HTMLButtonElement) tabList.append(tab);
    }

    for (const tab of this.#tabs) {
      if (!tab.dataset.tabTarget || !validPanelIds.has(tab.dataset.tabTarget)) tab.remove();
    }

    const tabs = this.#tabs;
    if (tabs.length === 0) return;

    const activeTab = tabs.find((tab) => tab.dataset.tabTarget === activePanelId) || tabs[0];
    this.#activateTab(activeTab, false);
  }

  #activateTab(activeTab, moveFocus = true) {
    if (activeTab.closest('tabs-component') !== this) return;

    for (const tab of this.#tabs) {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of this.#panels) {
      panel.hidden = panel.id !== activeTab.dataset.tabTarget;
    }

    if (moveFocus) activeTab.focus();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement) || tab.closest('tabs-component') !== this) return;

    this.#activateTab(tab, false);
  };

  #handleKeyDown = (event) => {
    if (!(event instanceof KeyboardEvent) || !(event.target instanceof HTMLButtonElement)) return;
    if (event.target.getAttribute('role') !== 'tab' || event.target.closest('tabs-component') !== this) return;

    const tabs = this.#tabs;
    const currentIndex = tabs.indexOf(event.target);
    if (currentIndex === -1) return;

    let nextIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.#activateTab(tabs[nextIndex]);
  };

  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-tab-item]');
    if (!(item instanceof HTMLElement) || item.closest('tabs-component') !== this) return;

    const panel = item.querySelector(':scope > [role="tabpanel"]');
    if (!(panel instanceof HTMLElement)) return;

    const tab = this.#tabs.find((candidate) => candidate.dataset.tabTarget === panel.id);
    if (tab) this.#activateTab(tab, false);
  };
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
